import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { useTranslation } from '@/src/i18n/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/src/components/ui/card'
import { Input } from '@/src/components/ui/input'
import { Button } from '@/src/components/ui/button'
import { Label } from '@/src/components/ui/label'
import { Textarea } from '@/src/components/ui/textarea'
import { Badge } from '@/src/components/ui/badge'
import { Checkbox } from '@/src/components/ui/checkbox'
import { listDrawings, getDrawing } from '@/src/api/drawing'
import { MergeDialog, type MergePreference } from '@/src/components/merge/MergeDialog'
import { JsonViewer } from '@/src/components/DataViewer/json-viewer'
import { resolveDrawingName, resolveDrawingNumber } from '@/src/lib/drawingHelpers'
import { toast } from 'sonner'

export const Route = createFileRoute('/_manage/manage/merge/')({
  validateSearch: (search: Record<string, unknown>) => ({
    leftId: typeof search.leftId === 'string' ? search.leftId : undefined,
    rightId: typeof search.rightId === 'string' ? search.rightId : undefined,
  }),
  component: ManageMergePage,
})

type SourceType = 'drawing' | 'payload'

function ManageMergePage() {
  const { t } = useTranslation('manage')
  const { t: tCommon } = useTranslation()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const [leftType, setLeftType] = useState<SourceType>('drawing')
  const [rightType, setRightType] = useState<SourceType>('drawing')
  const [leftSearch, setLeftSearch] = useState('')
  const [rightSearch, setRightSearch] = useState('')
  const [leftDrawingId, setLeftDrawingId] = useState<string>(search.leftId ?? '')
  const [rightDrawingId, setRightDrawingId] = useState<string>(search.rightId ?? '')
  const [leftPayload, setLeftPayload] = useState('{\n  \n}')
  const [rightPayload, setRightPayload] = useState('{\n  \n}')
  const [showMergeDialog, setShowMergeDialog] = useState(() => !!(search.leftId && search.rightId))
  const [mergePreference, setMergePreference] = useState<MergePreference>('ask')
  const [showOnlyDifferences, setShowOnlyDifferences] = useState(true)
  const [mergedResult, setMergedResult] = useState<Record<string, unknown> | null>(null)

  const selectLeftDrawing = (id: string) => {
    setLeftDrawingId(id)
    void navigate({
      search: (prev) => ({
        ...prev,
        leftId: id || undefined,
      }),
      replace: true,
    })
  }

  const selectRightDrawing = (id: string) => {
    setRightDrawingId(id)
    void navigate({
      search: (prev) => ({
        ...prev,
        rightId: id || undefined,
      }),
      replace: true,
    })
  }

  const { data: leftCandidates } = useQuery({
    queryKey: ['drawing', 'merge-left', leftSearch],
    queryFn: () => listDrawings({ data: { search: leftSearch || undefined, limit: 20, offset: 0, orderBy: 'createdAt', order: 'desc' } }),
    enabled: leftType === 'drawing',
  })

  const { data: rightCandidates } = useQuery({
    queryKey: ['drawing', 'merge-right', rightSearch],
    queryFn: () => listDrawings({ data: { search: rightSearch || undefined, limit: 20, offset: 0, orderBy: 'createdAt', order: 'desc' } }),
    enabled: rightType === 'drawing',
  })

  const { data: leftDrawing } = useQuery({
    queryKey: ['drawing', 'merge-left-selected', leftDrawingId],
    queryFn: () => getDrawing({ data: { id: leftDrawingId } }),
    enabled: leftType === 'drawing' && !!leftDrawingId,
  })

  const { data: rightDrawing } = useQuery({
    queryKey: ['drawing', 'merge-right-selected', rightDrawingId],
    queryFn: () => getDrawing({ data: { id: rightDrawingId } }),
    enabled: rightType === 'drawing' && !!rightDrawingId,
  })

  const leftPayloadError = useMemo(() => {
    if (leftType !== 'payload') return null
    try {
      const parsed = JSON.parse(leftPayload)
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return t('merge.payloadObjectOnly')
      return null
    } catch {
      return t('merge.invalidJson')
    }
  }, [leftPayload, leftType, t])

  const rightPayloadError = useMemo(() => {
    if (rightType !== 'payload') return null
    try {
      const parsed = JSON.parse(rightPayload)
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return t('merge.payloadObjectOnly')
      return null
    } catch {
      return t('merge.invalidJson')
    }
  }, [rightPayload, rightType, t])

  const leftData = useMemo(() => {
    if (leftType === 'drawing') {
      return (leftDrawing?.effectiveData ?? null) as Record<string, unknown> | null
    }
    if (leftPayloadError) return null
    return JSON.parse(leftPayload) as Record<string, unknown>
  }, [leftType, leftDrawing, leftPayload, leftPayloadError])

  const rightData = useMemo(() => {
    if (rightType === 'drawing') {
      return (rightDrawing?.effectiveData ?? null) as Record<string, unknown> | null
    }
    if (rightPayloadError) return null
    return JSON.parse(rightPayload) as Record<string, unknown>
  }, [rightType, rightDrawing, rightPayload, rightPayloadError])

  const canMerge = !!leftData && !!rightData && !leftPayloadError && !rightPayloadError
  const mergeViewActive = showMergeDialog && canMerge && !!leftData && !!rightData

  const copyMergedResult = async () => {
    if (!mergedResult) return
    await navigator.clipboard.writeText(JSON.stringify(mergedResult, null, 2))
    toast.success(t('merge.copied'))
  }

  if (mergeViewActive && leftData && rightData) {
    return (
      <div className="bg-background text-foreground">
        <AdminToolbar>
          <AdminToolbar.Left>

              <Button
                type="button"
                size="sm"
                variant={mergePreference === 'original' ? 'default' : 'outline'}
                onClick={() => setMergePreference('original')}
              >
                {tCommon('merge.preferOld')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant={mergePreference === 'updated' ? 'default' : 'outline'}
                onClick={() => setMergePreference('updated')}
              >
                {tCommon('merge.preferNew')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant={mergePreference === 'ask' ? 'default' : 'outline'}
                onClick={() => setMergePreference('ask')}
              >
                {tCommon('merge.askEach')}
              </Button>
          </AdminToolbar.Left>
          <AdminToolbar.Right>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">{tCommon('merge.preferenceLabel')}</span>
              <label className="ml-2 flex items-center gap-2 text-sm">
                <Checkbox
                  checked={showOnlyDifferences}
                  onCheckedChange={(checked) => setShowOnlyDifferences(!!checked)}
                />
                {tCommon('merge.showOnlyDifferences')}
              </label>
            </div>
          </AdminToolbar.Right>
        </AdminToolbar>

        <MergeDialog
          open={showMergeDialog}
          onOpenChange={setShowMergeDialog}
          original={leftData}
          updated={rightData}
          onMerge={(merged) => {
            setMergedResult(merged as Record<string, unknown>)
            setShowMergeDialog(false)
          }}
          onCancel={() => setShowMergeDialog(false)}
          showOnlyDifferences={true}
          showOnlyDifferencesValue={showOnlyDifferences}
          onShowOnlyDifferencesChange={setShowOnlyDifferences}
          mergePreferenceValue={mergePreference}
          onMergePreferenceChange={setMergePreference}
          showInBodyControls={false}
        />
      </div>
    )
  }

  return (
    <div className="bg-background text-foreground">
      <AdminToolbar>
        <AdminToolbar.Title>{t('merge.title')}</AdminToolbar.Title>
      </AdminToolbar>

      <div className="grid gap-4 lg:grid-cols-2">
        <SourceCard
          title={t('merge.leftSource')}
          sourceType={leftType}
          onSourceTypeChange={setLeftType}
          search={leftSearch}
          onSearchChange={setLeftSearch}
          candidates={leftCandidates?.drawings || []}
          selectedId={leftDrawingId}
          onSelectDrawing={selectLeftDrawing}
          payload={leftPayload}
          onPayloadChange={setLeftPayload}
          payloadError={leftPayloadError}
          drawingLabel={leftDrawing ? `${resolveDrawingName(leftDrawing) || t('dashboard.untitled')} (${resolveDrawingNumber(leftDrawing) || leftDrawing.shareId})` : ''}
          t={t}
        />

        <SourceCard
          title={t('merge.rightSource')}
          sourceType={rightType}
          onSourceTypeChange={setRightType}
          search={rightSearch}
          onSearchChange={setRightSearch}
          candidates={rightCandidates?.drawings || []}
          selectedId={rightDrawingId}
          onSelectDrawing={selectRightDrawing}
          payload={rightPayload}
          onPayloadChange={setRightPayload}
          payloadError={rightPayloadError}
          drawingLabel={rightDrawing ? `${resolveDrawingName(rightDrawing) || t('dashboard.untitled')} (${resolveDrawingNumber(rightDrawing) || rightDrawing.shareId})` : ''}
          t={t}
        />
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Button onClick={() => setShowMergeDialog(true)} disabled={!canMerge}>
          {t('merge.openDialog')}
        </Button>
        {!canMerge && <span className="text-sm text-muted-foreground">{t('merge.completeSources')}</span>}
      </div>

      {mergedResult && (
        <Card className="mt-4">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{t('merge.resultTitle')}</CardTitle>
            <Button size="sm" variant="outline" onClick={copyMergedResult}>
              {t('merge.copyResult')}
            </Button>
          </CardHeader>
          <CardContent>
            <JsonViewer
              data={mergedResult}
              showNumbers={true}
              shading={true}
              syntaxHighlighting={true}
              breaksLines={false}
              showControls={true}
            />
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function SourceCard({
  title,
  sourceType,
  onSourceTypeChange,
  search,
  onSearchChange,
  candidates,
  selectedId,
  onSelectDrawing,
  payload,
  onPayloadChange,
  payloadError,
  drawingLabel,
  t,
}: {
  title: string
  sourceType: SourceType
  onSourceTypeChange: (value: SourceType) => void
  search: string
  onSearchChange: (value: string) => void
  candidates: any[]
  selectedId: string
  onSelectDrawing: (value: string) => void
  payload: string
  onPayloadChange: (value: string) => void
  payloadError: string | null
  drawingLabel: string
  t: (key: string) => string
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant={sourceType === 'drawing' ? 'default' : 'outline'}
            onClick={() => onSourceTypeChange('drawing')}
          >
            {t('merge.sourceDrawing')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant={sourceType === 'payload' ? 'default' : 'outline'}
            onClick={() => onSourceTypeChange('payload')}
          >
            {t('merge.sourcePayload')}
          </Button>
        </div>

        {sourceType === 'drawing' ? (
          <>
            <div>
              <Label htmlFor={`${title}-search`}>{t('merge.searchDrawings')}</Label>
              <Input
                id={`${title}-search`}
                value={search}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder={t('merge.searchPlaceholder')}
              />
            </div>

            <div className="max-h-56 overflow-auto rounded border p-2 space-y-1">
              {candidates.length === 0 && (
                <div className="text-sm text-muted-foreground">{t('merge.noCandidates')}</div>
              )}
              {candidates.map((drawing) => {
                const label = resolveDrawingName(drawing) || t('dashboard.untitled')
                const sub = resolveDrawingNumber(drawing) || drawing.shareId
                const isSelected = selectedId === drawing.id
                return (
                  <button
                    key={drawing.id}
                    type="button"
                    onClick={() => onSelectDrawing(drawing.id)}
                    className={`w-full text-left rounded border px-2 py-1.5 transition-colors ${isSelected ? 'border-primary bg-primary/10' : 'hover:bg-muted'}`}
                  >
                    <div className="font-medium truncate">{label}</div>
                    <div className="text-xs text-muted-foreground truncate">{sub}</div>
                  </button>
                )
              })}
            </div>

            {selectedId && drawingLabel && (
              <Badge variant="secondary" className="max-w-full truncate">{drawingLabel}</Badge>
            )}
          </>
        ) : (
          <>
            <div>
              <Label htmlFor={`${title}-payload`}>{t('merge.payloadLabel')}</Label>
              <Textarea
                id={`${title}-payload`}
                value={payload}
                onChange={(event) => onPayloadChange(event.target.value)}
                className="min-h-56 font-mono"
              />
            </div>
            {payloadError && <div className="text-sm text-destructive">{payloadError}</div>}
          </>
        )}
      </CardContent>
    </Card>
  )
}
